import React, { createContext, useContext, useState, useEffect, useMemo} from 'react';
import axiosInstance from '../utils/axiosInstance';
import { POST_LOGIN, POST_OTP } from '../config/api';
import { parseJwt, encode } from '../utils/sharedUtil';
import { storageTokenKeyName, temporaryStorageTokenKeyName } from '../config/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { publicRoutes } from '../routes/AllRoutes';
import { AuthContextProviderPropTypes } from '../utils/propTypes';

const defaultProvider = {
    user: null,
    isAuthenticated: false,
    onLogin: () => new Promise(),
    onLogOut: () => { },
    onOTP: () => { }
}
// Create the Auth Context
const AuthContext = createContext(defaultProvider);

// AuthProvider Component
export const AuthContextProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isAuthenticated, setIsAuthenticated] = useState(defaultProvider.isAuthenticated);
    const [user, setUser] = useState(defaultProvider.user);


    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem(storageTokenKeyName);
            if (storedToken) {
                setIsAuthenticated(true);
                const userData = parseJwt(storedToken);
                setUser(userData);

                if (location.pathname == '/' || location.pathname == '/login' || location.pathname.startsWith("/reset-password") || location.pathname.startsWith("/otp") || location.pathname.startsWith("/forget-password")) {
                    navigate('/planner');
                }
                else {
                    navigate(`${location.pathname}${location.search}`);
                }
            }
            else {
                setUser(null);
                setIsAuthenticated(false);
                if (!(publicRoutes.find(item => location.pathname.includes(item.name)))) {
                    navigate('/login');
                }
                else {
                    navigate(`${location.pathname}${location.search}`);
                }
            }
        }
        initAuth();
    }, []);


    const onLoginHandler = (request) => {
        return axiosInstance.post(POST_LOGIN, request)
            .then(function (response) {
                if (response.data?.responseCode === 200) {
                    if (response.data.responseData.changePasswordEnabled) {
                        localStorage.setItem(temporaryStorageTokenKeyName, response.data.responseData.token);
                        navigate(`/reset-password/${response.data.responseData.token}`);
                    }
                    else if (response.data.responseData.twoFactorEnabled) {
                        const userData = parseJwt(response.data.responseData.token);

                        let queryParms = encode({
                            email: userData.email,
                            message: response.data.responseMessage,
                            type: 'login'
                        });

                        localStorage.setItem(temporaryStorageTokenKeyName, response.data.responseData.token);
                        navigate(`/otp/${response.data.responseData.token}?data=${queryParms}`);
                    }
                    else if (response.data.responseData.token) {
                        localStorage.setItem(storageTokenKeyName, response.data.responseData.token);
                        const userData = parseJwt(response.data.responseData.token);
                        setUser(userData);
                        navigate(`/planner`);
                        setIsAuthenticated(true);
                    }
                }
                return response.data;
            })
            .catch((error) => {
                console.log(error)
                throw error;
            });
    };

    const onOTPHandler = (request) => {
        return axiosInstance.post(POST_OTP, request)
            .then(function (response) {
                if (response.data?.responseCode === 200) {
                    if (response.data.responseData?.changePasswordEnabled) {
                        localStorage.setItem(temporaryStorageTokenKeyName, response.data.responseData.token);
                        navigate(`/reset-password/${response.data.responseData.token}`);
                    }
                    else if (response.data?.responseData?.token) {
                        localStorage.removeItem(temporaryStorageTokenKeyName)
                        localStorage.setItem(storageTokenKeyName, response.data.responseData.token);
                        const userData = parseJwt(response.data.responseData.token);
                        setUser(userData);
                        navigate('/planner');
                        setIsAuthenticated(true);
                    }
                    return response.data;
                }
                else{
                    return response.data
                }
            })
            .catch((error) => {
                console.log(error)
                throw error;
            });
    }

    const onLogOutHandler = () => {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem(temporaryStorageTokenKeyName);
        localStorage.removeItem(storageTokenKeyName);
        navigate('/login');
    };

    const authValue = useMemo(() => ({ user, isAuthenticated, onLogin:onLoginHandler, onLogOut:onLogOutHandler, onOTP:onOTPHandler }), [isAuthenticated]);

    return (
        <AuthContext.Provider value={authValue}>
            {children}
        </AuthContext.Provider>
    );
};

AuthContextProvider.propTypes = AuthContextProviderPropTypes;

// Custom hook to use AuthContext
export const useAuth = () => useContext(AuthContext);